import React, { CSSProperties } from "react";
import { Header, Icon, Label, Segment } from "semantic-ui-react";

// Serializers
import PatientAppointmentSerializer from "issara-sdk/types/PatientAppointmentSerializer_apps_QUE";

const PRIMARY_COLOR = "#F382A0";

const APPOINTMENT_STATUS_LIST = [
  { id: 1, value: "รอยืนยัน" },
  { id: 2, value: "ยืนยันแล้ว" },
  { id: 3, value: "ถูกเลื่อนนัด" },
  { id: 5, value: "ยกเลิก" },
];

const styles = {
  sectionHeader: {
    background: PRIMARY_COLOR,
    padding: "8px 13px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    margin: 0,
  } as CSSProperties,
  listItem: {
    padding: "10px 9px",
    position: "relative",
    margin: 0,
    borderColor: PRIMARY_COLOR,
    border: "solid 1px #F382A0",
  } as CSSProperties,
  requestStatus: {
    backgroundColor: "rgba(255, 187, 123, 0.86)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  confirmStatus: {
    backgroundColor: "rgba(36, 197, 36, 0.3)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  postponeStatus: {
    backgroundColor: "rgba(255, 227, 104, 0.86)",
    color: "rgba(0, 0, 0, 1)",
  } as CSSProperties,
  cancelStatus: {
    backgroundColor: "rgba(255, 4, 4, 0.08)",
    color: "rgba(255, 0, 0, 1)",
  } as CSSProperties,
};

type BookingSectionProps = {
  appointments?: PatientAppointmentSerializer[];
  isCollapsed: boolean;
  onToggle: () => void;
};

const splitDateTime = (input?: string | null): { date: string; time: string } => {
  if (!input) return { date: "", time: "" };

  const match = input.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s*\[(\d{1,2}:\d{2})\]$/);

  if (!match) {
    return { date: input.trim(), time: "" };
  }

  const [, date, time] = match;
  return { date, time };
};

const getStatusDisplay = (status?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8) => {
  if (!status)
    return {
      text: "",
      style: {},
    };

  const statusMap: Record<number, CSSProperties> = {
    1: styles.requestStatus,
    2: styles.confirmStatus,
    3: styles.postponeStatus,
    5: styles.cancelStatus,
  };

  const item = APPOINTMENT_STATUS_LIST.find((s) => s.id === status);

  return {
    text: item?.value || "",
    style: statusMap[status] || {},
  };
};

const BookingSection: React.FC<BookingSectionProps> = (props) => {
  const appointments = props.appointments || [];
  const filteredAppointments = appointments.filter((item) =>
    [1, 2, 3, 5].includes(item.status as number)
  );

  return (
    <Segment.Group style={{ marginBottom: "10px" }}>
      {/* Booking Header */}
      <Segment inverted style={styles.sectionHeader} onClick={props.onToggle}>
        <Header as="h5" inverted style={{ margin: 0, fontSize: "13px" }}>
          ข้อมูลนัดหมาย
        </Header>
        <Icon name={props.isCollapsed ? "angle down" : "angle up"} />
      </Segment>

      {/* Booking Content */}
      {!props.isCollapsed &&
        filteredAppointments.map((appointment, appointmentIndex) => (
          <Segment
            key={appointment.id}
            style={{
              ...styles.listItem,
              marginTop: appointmentIndex === 0 ? 0 : "5px",
              borderTop: appointmentIndex === 0 ? "none" : "solid 1px #F382A0",
              borderRadius: appointmentIndex === 0 ? "0 0 5px 5px" : "5px",
            }}
          >
            <Label
              style={{
                position: "absolute",
                top: "8px",
                right: -1,
                fontSize: "10px",
                borderRadius: "5px 0 0 5px",
                ...getStatusDisplay(appointment.status).style,
              }}
            >
              {getStatusDisplay(appointment.status).text}
            </Label>
            <div style={{ fontWeight: 500, fontSize: "11px", marginBottom: "8px" }}>
              <strong>{appointment.doctor_full_name}</strong>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "5px",
                fontSize: "11px",
              }}
            >
              <span style={{ marginRight: "5px" }}>วันเวลาที่นัดหมาย : </span>
              <Icon name="calendar" style={{ color: PRIMARY_COLOR, margin: "-11px 5px 0 0" }} />
              <span>{splitDateTime(appointment.estimated_at).date || "-"}</span>
              <Icon name="clock" style={{ color: PRIMARY_COLOR, margin: "-11px 5px 0 10px" }} />
              <span>{splitDateTime(appointment.estimated_at).time || "-"}</span>
            </div>
            <div
              style={{
                marginTop: "5px",
                fontSize: "11px",
                display: "flex",
                alignItems: "flex-start"
              }}
            >
              <span style={{ whiteSpace: "nowrap" }}>สถานที่รับบริการ </span>
              <span style={{ marginLeft: "7px", marginRight: "5px", flex: 1, wordBreak: "break-word" }}>:</span>
              <span>{appointment.division_location || "-"}</span>
            </div>
          </Segment>
        ))}
    </Segment.Group>
  );
};

export default BookingSection;
